/**
 * Hook React pour la gestion de la sécurité
 * RÉUTILISABILITÉ : 100% - Compatible Next.js et React
 *
 * Ce hook encapsule la logique de sécurité :
 * - Gestion des paramètres de sécurité
 * - Surveillance des tentatives de connexion
 * - Gestion de la liste noire IP
 * - Logs de sécurité
 */

  // Client Component pour Next.js

import { useState } from 'react';
import { useAuthStore } from '../store/authStore';
import { securityApi } from '../../infrastructure/api';
import type {
  SecuritySettings,
  SecurityLog,
  LoginAttempt,
  UpdateSecuritySettingsData,
} from '../../application/dto/dtos';

/**
 * Interface du hook useSecurity
 */
export interface UseSecurityReturn {
  // État
  isLoading: boolean;
  error: string | null;

  // Actions - Paramètres
  getSecuritySettings: () => Promise<SecuritySettings | null>;
  updateSecuritySettings: (data: UpdateSecuritySettingsData) => Promise<void>;

  // Actions - Blacklist IP
  blockIP: (adresseIP: string, raison: string, typeBlocage?: 'TEMPORAIRE' | 'PERMANENT') => Promise<void>;

  // Actions - Logs
  getSecurityLogs: (limite?: number) => Promise<SecurityLog[]>;
  getLoginAttempts: (limite?: number) => Promise<LoginAttempt[]>;

  // Utilitaires
  clearError: () => void;
}

/**
 * Hook pour la gestion de la sécurité
 *
 * @example
 * ```typescript
 * function SecuritySettingsPage() {
 *   const {
 *     getSecuritySettings,
 *     updateSecuritySettings,
 *     isLoading,
 *     error
 *   } = useSecurity();
 *
 *   useEffect(() => {
 *     getSecuritySettings();
 *   }, []);
 *
 *   const handleUpdate = async (settings: UpdateSecuritySettingsData) => {
 *     try {
 *       await updateSecuritySettings(settings);
 *       // Succès
 *     } catch (err) {
 *       // Erreur déjà gérée
 *     }
 *   };
 *
 *   return ReactComponent; // JSX omitted for brevity
 * }
 * ```
 */
export function useSecurity(): UseSecurityReturn {
  const user = useAuthStore((state) => state.user);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Récupérer les paramètres de sécurité
   */
  const getSecuritySettings = async (): Promise<SecuritySettings | null> => {
    if (!user?.id) {
      setError('Utilisateur non connecté');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await securityApi.getSecuritySettings({
        idUtilisateur: user.id,
      });

      if (!response.succes || !response.data) {
        throw new Error(response.message || 'Impossible de récupérer les paramètres de sécurité');
      }

      setIsLoading(false);
      return response.data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur de récupération des paramètres';
      setError(message);
      setIsLoading(false);
      return null;
    }
  };

  /**
   * Mettre à jour les paramètres de sécurité
   */
  const updateSecuritySettings = async (data: UpdateSecuritySettingsData): Promise<void> => {
    if (!user?.id) {
      setError('Utilisateur non connecté');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await securityApi.updateSecuritySettings({
        ...data,
        idUtilisateur: user.id,
      });

      if (!response.succes) {
        throw new Error(response.message || 'Échec de la mise à jour des paramètres');
      }

      setIsLoading(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur de mise à jour des paramètres';
      setError(message);
      setIsLoading(false);
      throw err;
    }
  };

  /**
   * Bloquer une adresse IP
   */
  const blockIP = async (
    adresseIP: string,
    raison: string,
    typeBlocage: 'TEMPORAIRE' | 'PERMANENT' = 'PERMANENT'
  ): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await securityApi.blockIP({
        adresseIP,
        raison,
        typeBlocage,
      });

      if (!response.succes) {
        throw new Error(response.message || 'Échec du blocage de l\'IP');
      }

      setIsLoading(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors du blocage de l\'IP';
      setError(message);
      setIsLoading(false);
      throw err;
    }
  };

  /**
   * Récupérer les logs de sécurité
   */
  const getSecurityLogs = async (limite: number = 50): Promise<SecurityLog[]> => {
    if (!user?.id) {
      setError('Utilisateur non connecté');
      return [];
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await securityApi.getSecurityLogs({
        idUtilisateur: user.id,
        limite,
      });

      if (!response.succes || !response.data) {
        throw new Error(response.message || 'Impossible de récupérer les logs de sécurité');
      }

      setIsLoading(false);
      return response.data.logs;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur de récupération des logs';
      setError(message);
      setIsLoading(false);
      return [];
    }
  };

  /**
   * Récupérer les tentatives de connexion
   */
  const getLoginAttempts = async (limite: number = 20): Promise<LoginAttempt[]> => {
    if (!user?.id) {
      setError('Utilisateur non connecté');
      return [];
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await securityApi.getLoginAttempts({
        idUtilisateur: user.id,
        limite,
      });

      if (!response.succes || !response.data) {
        throw new Error(response.message || 'Impossible de récupérer les tentatives de connexion');
      }

      setIsLoading(false);
      return response.data.tentatives;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur de récupération des tentatives';
      setError(message);
      setIsLoading(false);
      return [];
    }
  };

  /**
   * Effacer l'erreur
   */
  const clearError = () => {
    setError(null);
  };

  return {
    // État
    isLoading,
    error,

    // Actions - Paramètres
    getSecuritySettings,
    updateSecuritySettings,

    // Actions - Blacklist IP
    blockIP,

    // Actions - Logs
    getSecurityLogs,
    getLoginAttempts,

    // Utilitaires
    clearError,
  };
}

/**
 * Hook pour détecter les activités suspectes
 * Analyse les tentatives de connexion et retourne un indicateur de risque
 *
 * @param attempts - Liste des tentatives de connexion
 *
 * @example
 * ```typescript
 * function SecurityDashboard() {
 *   const { getLoginAttempts } = useSecurity();
 *   const [attempts, setAttempts] = useState<LoginAttempt[]>([]);
 *   const suspiciousActivity = useSuspiciousActivityDetection(attempts);
 *
 *   useEffect(() => {
 *     getLoginAttempts().then(setAttempts);
 *   }, []);
 *
 *   return ReactComponent; // JSX: alert if suspiciousActivity.isDetected
 * }
 * ```
 */
export function useSuspiciousActivityDetection(attempts: LoginAttempt[]): {
  isDetected: boolean;
  reason: string | null;
  riskLevel: 'low' | 'medium' | 'high';
} {
  if (!attempts || attempts.length === 0) {
    return { isDetected: false, reason: null, riskLevel: 'low' };
  }

  // Analyser les tentatives des dernières 24h
  const now = new Date();
  const last24h = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const recentAttempts = attempts.filter((a) => new Date(a.dateHeure) > last24h);

  // Compter les échecs
  const failedAttempts = recentAttempts.filter((a) => !a.succes);

  // Niveau 1 : Plus de 5 échecs en 24h
  if (failedAttempts.length > 5) {
    return {
      isDetected: true,
      reason: `${failedAttempts.length} tentatives de connexion échouées en 24h`,
      riskLevel: 'high',
    };
  }

  // Niveau 2 : Plus de 3 échecs en 24h
  if (failedAttempts.length > 3) {
    return {
      isDetected: true,
      reason: `${failedAttempts.length} tentatives de connexion échouées en 24h`,
      riskLevel: 'medium',
    };
  }

  // Niveau 3 : Multiples IP différentes
  const uniqueIPs = new Set(recentAttempts.map((a) => a.adresseIP));
  if (uniqueIPs.size > 3) {
    return {
      isDetected: true,
      reason: `Connexions depuis ${uniqueIPs.size} adresses IP différentes`,
      riskLevel: 'medium',
    };
  }

  return { isDetected: false, reason: null, riskLevel: 'low' };
}

/**
 * Hook pour formater et grouper les logs de sécurité par date
 *
 * @param logs - Liste des logs de sécurité
 *
 * @example
 * ```typescript
 * function SecurityLogsTimeline() {
 *   const { getSecurityLogs } = useSecurity();
 *   const [logs, setLogs] = useState<SecurityLog[]>([]);
 *   const groupedLogs = useSecurityLogsGrouped(logs);
 *
 *   useEffect(() => {
 *     getSecurityLogs().then(setLogs);
 *   }, []);
 *
 *   return ReactComponent; // JSX: render grouped logs by date
 * }
 * ```
 */
export function useSecurityLogsGrouped(
  logs: SecurityLog[]
): Record<string, SecurityLog[]> {
  const grouped: Record<string, SecurityLog[]> = {};

  logs.forEach((log) => {
    const date = new Date(log.dateCreation).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    if (!grouped[date]) {
      grouped[date] = [];
    }

    grouped[date].push(log);
  });

  return grouped;
}
